import { Alert, Box, LinearProgress, Typography } from '@mui/material'
import { FileText, History, Map as MapIcon, Rows3 } from 'lucide-react'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRun } from '../../api/hooks'
import { EmptyState } from '../../components/primitives'
import { SkeletonScreen } from '../../components/Skeleton'
import type { RegionDetail } from '../../api/types'
import { num } from '../../lib/format'
import { tokens } from '../../theme'
import { ResultsMap } from '../results/ResultsMap'
import { ResultsReport } from '../results/ResultsReport'
import { ResultsTable } from '../results/ResultsTable'
import { RunSummaryPanel } from '../results/RunSummaryPanel'

/**
 * A region's polygons, and what the checks made of them.
 *
 * The verdicts belong to a run — a plot's status is written by one — so
 * this reads the region's latest and hands it to the same map and table the
 * results screen uses. Building a second pair that rendered the same rows a
 * little differently would be two things to keep in step and one of them
 * would drift.
 *
 * It is also where "Run validation" lands: while the run is going this shows
 * its progress, then its results in place.
 */
export function RegionPolygons({
  detail, view, checkFilter, onCheckFilter,
}: {
  detail: RegionDetail
  view: 'map' | 'table' | 'report'
  checkFilter: string[]
  onCheckFilter: (keys: string[]) => void
}) {
  // A run in flight takes over the screen until it finishes. latest_run
  // still names the previous completed one, which would otherwise be shown
  // as though nothing had happened.
  const runId = detail.active_run?.id ?? detail.latest_run?.id
  const known = detail.active_run ?? detail.latest_run
  const { data: run, isLoading } = useRun(runId)

  // The region's counts and status change when its run finishes.
  const qc = useQueryClient()
  const settled = run && run.status !== 'queued' && run.status !== 'running'
  useEffect(() => {
    if (settled && known?.status !== run.status) {
      qc.invalidateQueries({ queryKey: ['region', detail.region.id] })
      qc.invalidateQueries({ queryKey: ['region-fields', detail.region.id] })
      qc.invalidateQueries({ queryKey: ['regions'] })
    }
  }, [settled, run?.status, known?.status, detail.region.id, qc, run])

  if (!known) {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 8 }}>
        <EmptyState
          title="These polygons have not been checked yet"
          body={
            detail.region.plot_count > 0
              ? 'Run the checks over this region and the verdicts will appear here.'
              : 'Upload a file into this region or draw some plots, then run the checks.'
          }
        />
      </Box>
    )
  }

  if (isLoading || !run) {
    return <SkeletonScreen wide />
  }

  if (run.status === 'queued' || run.status === 'running') {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
        <Box sx={{ width: 420, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {/* Every polygon in the region, not only the latest file's. */}
            Running {num(Object.keys(run.config?.checks ?? {}).length)} checks over the {num(run.feature_count)} polygons in this region…
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 5 }}>
            {run.stage ? `Now: ${run.stage.replace(/_/g, ' ')}.` : 'Queued.'} Results open here as soon as it finishes.
          </Typography>
          <LinearProgress variant="determinate" value={Math.round(run.progress * 100)} />
        </Box>
      </Box>
    )
  }

  if (run.status === 'failed') {
    return (
      <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', p: 8 }}>
        <Alert severity="error" sx={{ maxWidth: 560 }}>
          The run did not finish. {run.error}
        </Alert>
      </Box>
    )
  }

  if (view === 'report') {
    return (
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflowY: 'auto' }}>
        <ResultsReport run={run} />
      </Box>
    )
  }

  const toggleCheck = (key: string) =>
    onCheckFilter(checkFilter.includes(key) ? checkFilter.filter((k) => k !== key) : [...checkFilter, key])

  return (
    <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
      <RunSummaryPanel
        run={run}
        activeChecks={checkFilter}
        onToggleCheck={toggleCheck}
      />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {view === 'map' ? (
          <ResultsMap run={run} checkFilter={checkFilter} onCheckFilter={onCheckFilter} />
        ) : (
          <ResultsTable run={run} checkFilter={checkFilter} onClearFilter={() => onCheckFilter([])} />
        )}
      </Box>
    </Box>
  )
}

export type RegionView = 'yield' | 'map' | 'table' | 'report' | 'history'

/**
 * Map, table, the checks report, or the yield benchmark.
 *
 * Yield sits alongside the other two because it is a third view of the same
 * polygons, not a different place — and leaving it out of this switch was
 * what made the region screen feel like it only did one thing.
 */
export function ViewSwitch({
  view, onView,
}: {
  view: RegionView
  onView: (view: RegionView) => void
}) {
  const item = (value: RegionView, label: string, Icon?: typeof MapIcon) => {
    const active = view === value
    return (
      <Box
        component="button"
        type="button"
        onClick={() => onView(value)}
        aria-pressed={active}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.25, border: 0,
          cursor: 'pointer', borderRadius: `${tokens.radius.sm}px`,
          font: `500 13px ${tokens.font.sans}`,
          backgroundColor: active ? 'background.paper' : 'transparent',
          boxShadow: active ? tokens.shadow[1] : 'none',
          color: active ? 'text.primary' : 'text.secondary',
        }}
      >
        {Icon && <Icon size={14} strokeWidth={1.5} />}
        {label}
      </Box>
    )
  }

  return (
    <Box
      role="group"
      aria-label="View"
      sx={{
        display: 'flex', p: 0.5, ml: 2, flex: 'none',
        borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100,
      }}
    >
      {item('map', 'Map', MapIcon)}
      {item('table', 'Table', Rows3)}
      {item('report', 'Report', FileText)}
      {item('yield', 'Yield')}
      {item('history', 'History', History)}
    </Box>
  )
}
